import {FetchPolicy} from '@apollo/client';
import gql from 'graphql-tag';

import {DataModificationsHeader} from 'audit/DataModifications';
import {Location, ParentStorableData, StorableSummary} from 'constants/SampleConstants';
import {TagsDict} from 'folder-items/FileConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {LocationSchemaIdsQuery} from 'samples/__generated/LocationSchemaIdsQuery';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as net from 'util/Net';

export interface LocationCapacityConfigurationParams {
  allowsNonLocationChildren?: boolean;
  allowedChildStorableSchemaApiIds?: Array<string> | null;
  capacity?: number | null;
}

export interface CreateLocationParams extends LocationCapacityConfigurationParams {
  barcode?: string;
  parentStorableData?: ParentStorableData;
  tags?: TagsDict;
}

export function createLocation(
  locationSchemaId: string,
  name: string,
  createLocationParams: CreateLocationParams = {}
): JQueryPromise<Location> {
  const data = {
    locationSchemaId,
    name,
    barcode: createLocationParams.barcode,
    parentStorableData: createLocationParams.parentStorableData,
    tags: createLocationParams.tags,
    allowsNonLocationChildren: createLocationParams.allowsNonLocationChildren,
    allowedChildStorableSchemaApiIds: createLocationParams.allowedChildStorableSchemaApiIds,
    capacity: createLocationParams.capacity,
  };
  return net.postJSON({
    url: '/1/api/locations',
    data,
  });
}

export interface FetchLocationResponse {
  location: Location;
  ancestralLocations: Array<StorableSummary>;
}

export function fetchLocation(locationId: string): JQueryPromise<FetchLocationResponse> {
  return net.getJSON(`/1/api/locations/${locationId}`);
}

// All attributes are optional because only changed attributes are included
export interface UpdateLocationParams extends LocationCapacityConfigurationParams {
  name?: string;
  barcode?: string;
  parentStorableData?: ParentStorableData;
  tags?: TagUpdateObject;
}

export function updateLocation(
  locationId: string,
  data: UpdateLocationParams,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<Location> {
  return net.patchJSON({url: `/1/api/locations/${locationId}`, data, extraHeaders: dataModsHeader});
}

// returns readable location schemas only
export const LOCATION_SCHEMA_IDS_QUERY = gql`
  query LocationSchemaIdsQuery @skipAudit @engComponent(name: INVENTORY) {
    locationSchemas {
      ... on LocationSchemaNode {
        id
      }
    }
  }
`;

export async function fetchLocationSchemaIds(fetchPolicy?: FetchPolicy): Promise<Array<string>> {
  const result = await doQuerySafe<LocationSchemaIdsQuery>({query: LOCATION_SCHEMA_IDS_QUERY}, {fetchPolicy});
  return result.locationSchemas.map(({id}) => id);
}
